import { decodeToArrayBuffer, encodeArrayBuffer } from '@/idv2/utils/webauthn';

import type { BiometricRegisterChallengeJson, RegisterPasskeyError } from '../types';

/**
 * Registers a passkey on the device using the WebAuthn API.
 * Takes a server challenge (with base64url-encoded fields) and returns
 * the credential response as a JSON string.
 */
export const registerPasskeyOnDevice = async (challenge: string): Promise<string> => {
  const { publicKey: serverPublicKey } = JSON.parse(challenge) as BiometricRegisterChallengeJson;

  // Convert server wire format to WebAuthn API format
  const publicKey: PublicKeyCredentialCreationOptions = {
    ...serverPublicKey,
    challenge: decodeToArrayBuffer(serverPublicKey.challenge),
    user: {
      ...serverPublicKey.user,
      id: decodeToArrayBuffer(serverPublicKey.user.id),
    },
  };

  const credential = await window.navigator.credentials.create({ publicKey });

  if (!credential) {
    throw new Error('Failed to create passkey credential');
  }

  const publicKeyCredential = credential as PublicKeyCredential;
  const attestationResponse = publicKeyCredential.response as AuthenticatorAttestationResponse;

  return JSON.stringify({
    rawId: encodeArrayBuffer(publicKeyCredential.rawId),
    id: publicKeyCredential.id,
    type: 'public-key',
    response: {
      clientDataJSON: encodeArrayBuffer(attestationResponse.clientDataJSON),
      attestationObject: encodeArrayBuffer(attestationResponse.attestationObject),
    },
  });
};

/**
 * Wraps registerPasskeyOnDevice to track timing and return a RegisterPasskeyError on failure.
 */
export const registerPasskeyWithTiming = async (
  challenge: string,
): Promise<{ challengeResponse: string } | RegisterPasskeyError> => {
  const startTime = Date.now();

  try {
    const challengeResponse = await registerPasskeyOnDevice(challenge);
    return { challengeResponse };
  } catch (error) {
    const elapsedTimeInOsPromptMs = Date.now() - startTime;
    return {
      kind: 'registerPasskeyError',
      elapsedTimeInOsPromptMs,
      error,
    };
  }
};
